import React from 'react';
import { Alert, Button, MenuItem, Stack, TextField } from '@mui/material';
import { IMaskInput } from 'react-imask';

import DialogJsonField from './DialogJsonField';
import { AppDataContext } from '../../context/Context';
import { asFlatStringObject } from '../../utils/jsonObjectValue';
import {
    cardDigits,
    extraKeys,
    fieldProblem,
    presetsFromTemplates,
    templateFor,
    templateValue
} from '../../utils/walletRequisites';
import i18n from '../../i18n';

const TEMPLATE_MODE = 'template';

// 16 digits in fours, up to 19; what is emitted is the digits alone (unmask).
const CardNumberInput = React.forwardRef(function CardNumberInput(props, ref) {
    const { onChange, name, ...other } = props;
    return (
        <IMaskInput
            {...other}
            name={name}
            mask="0000 0000 0000 0000[ 000]"
            unmask
            inputRef={ref}
            onAccept={(value) => onChange({ target: { name, value } })}
        />
    );
});

/**
 * A wallet's receiverInfo, as the fields of its gateway's requisites template.
 *
 * When the gateway picked in the form names a template (x-backend V26), the
 * **Template** mode is the editor: one labelled input per field, in the template's
 * order — a card number masked in fours, a network picked from its options — each
 * with its problem under it, the same verdict the cabinet will give
 * (utils/walletRequisites). Fields, the free key/value rows, is not offered there:
 * the cabinet refuses any key outside the template. Raw stays, for looking.
 *
 * Keys the value has outside the template — a wallet moved here from another
 * gateway, or written before it had one — are never dropped behind the operator's
 * back: editing a field keeps them, a warning names them, and one button removes
 * them. Until then Save is held (WalletsPage's validate).
 *
 * With no gateway yet, a gateway without a template, or the templates not loaded,
 * this is DialogJsonField as it was, its preset chips the templates' key names.
 */
export default class DialogWalletRequisitesField extends DialogJsonField {
    static contextType = AppDataContext;

    constructor(props) {
        super(props);
        this.state = { ...this.state, touched: {} };
    }

    componentDidMount() {
        this.followTemplate();
        // The templates load with the page; a dialog opened first catches up here,
        // re-rendered so the Template toggle appears whichever mode is open.
        this.unsubscribe = this.store()?.subscribe?.(() => {
            this.followTemplate();
            this.forceUpdate();
        });
    }

    componentDidUpdate() {
        this.followTemplate();
    }

    componentWillUnmount() {
        this.unsubscribe?.();
    }

    /**
     * Opens on the template whenever the gateway has one, and leaves it when the
     * gateway is changed to one that has none. Raw, chosen by hand, is left alone.
     */
    followTemplate() {
        const template = this.template();
        const { mode } = this.state;
        if (template && mode === 'fields') {
            this.setState({ mode: TEMPLATE_MODE });
        } else if (!template && mode === TEMPLATE_MODE) {
            this.setState({ mode: asFlatStringObject(this.props.value) ? 'fields' : 'raw' });
        }
    }

    store() {
        return this.props.context || this.context;
    }

    templates() {
        return this.store()?.getState().walletRequisitesTemplates || [];
    }

    template() {
        const gateways = this.store()?.getState().controllersData?.publicGateways;
        return templateFor(this.props.data?.gatewayId, gateways, this.templates());
    }

    fieldsModeOffered() {
        return !this.template();
    }

    presets() {
        return presetsFromTemplates(this.templates());
    }

    extraModes() {
        if (!this.template()) return [];
        return [{ value: TEMPLATE_MODE, label: i18n.t('walletRequisites.modeTemplate') }];
    }

    helperLine(mode, fieldsPossible) {
        if (mode !== TEMPLATE_MODE) {
            const template = this.template();
            if (!template) return super.helperLine(mode, fieldsPossible);
            return this.props.error || i18n.t('walletRequisites.rawHelper');
        }
        const template = this.template();
        // The warning above already says it, with its Remove button.
        if (template && extraKeys(template, this.current()).length) return '';
        return this.props.error || i18n.t('walletRequisites.templateHelper', {
            template: i18n.t(`walletRequisites.templates.${template?.template}`)
        });
    }

    current() {
        return asFlatStringObject(this.props.value) || {};
    }

    handleRequisiteChange = (key, text) => {
        const template = this.template();
        this.emitValue(templateValue(template, { ...this.current(), [key]: text }));
    };

    handleRequisiteBlur = (key) => {
        this.setState((s) => ({ touched: { ...s.touched, [key]: true } }));
        this.props.onBlur?.();
    };

    removeExtra = () => {
        this.emitValue(templateValue(this.template(), this.current(), false));
    };

    /** A field's problem, once it has been left or Save was tried — not while it is first typed. */
    problemText(field, text) {
        if (!this.state.touched[field.key] && !this.props.error) return '';
        const problem = fieldProblem(field, text);
        return problem ? i18n.t(problem.key, problem.params) : '';
    }

    renderRequisite(field, disabled) {
        const text = this.current()[field.key] ?? '';
        const problem = this.problemText(field, text);
        const common = {
            id: `${this.props.field.id}-${field.key}`,
            size: 'small',
            fullWidth: true,
            required: field.required,
            label: i18n.t(`walletRequisites.fields.${field.key}`),
            disabled,
            error: Boolean(problem),
            helperText: problem || ' ',
            onBlur: () => this.handleRequisiteBlur(field.key),
            onChange: (e) => this.handleRequisiteChange(field.key, e.target.value)
        };

        if (field.format === 'card_number') {
            return (
                <TextField
                    {...common}
                    value={cardDigits(text)}
                    placeholder="0000 0000 0000 0000"
                    InputProps={{
                        inputComponent: CardNumberInput,
                        sx: { fontFamily: 'monospace', letterSpacing: 1 }
                    }}
                    inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
                />
            );
        }
        if (field.format === 'choice') {
            return (
                <TextField {...common} select value={text}>
                    {!field.required && <MenuItem value="">{i18n.t('walletRequisites.none')}</MenuItem>}
                    {(field.options || []).map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField>
            );
        }
        if (field.format === 'phone') {
            return (
                <TextField
                    {...common}
                    value={text}
                    placeholder="+998 90 123 45 67"
                    inputProps={{ inputMode: 'tel', autoComplete: 'off' }}
                />
            );
        }
        return (
            <TextField
                {...common}
                value={text}
                InputProps={field.format === 'crypto_address' ? { sx: { fontFamily: 'monospace' } } : undefined}
                inputProps={{ autoComplete: 'off' }}
            />
        );
    }

    renderModeBody(mode) {
        if (mode !== TEMPLATE_MODE) return super.renderModeBody(mode);
        const template = this.template();
        if (!template) return null;

        const disabled = Boolean(this.props.field.onlyRead) || Boolean(this.props.disabled);
        const extra = extraKeys(template, this.current());

        return (
            <Stack spacing={0.5} sx={{ pt: 1 }}>
                {template.fields.map((field) => (
                    <React.Fragment key={field.key}>{this.renderRequisite(field, disabled)}</React.Fragment>
                ))}
                {extra.length > 0 && (
                    <Alert
                        severity="warning"
                        variant="outlined"
                        action={
                            <Button color="inherit" size="small" disabled={disabled} onClick={this.removeExtra}>
                                {i18n.t('walletRequisites.removeExtra')}
                            </Button>
                        }
                    >
                        {i18n.t('walletRequisites.extraKeys', { keys: extra.join(', ') })}
                    </Alert>
                )}
            </Stack>
        );
    }
}
